import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import { message } from 'antd';
import {
  applyAutoCorrelation,
  getLatestAutoCorrelation,
  runAutoCorrelation
} from '../../api/data';
import {
  AutoCombinationKey,
  AutoCorrelationReport,
  AutoCorrelationScopeMode,
  CorrelationEndpointSource,
  SelectedFziFunctionsOptions,
  XAxisVariable,
  ZoneValues
} from '../../@types/projects/types';
import { useAppContext } from '../AppContext';
import { useSelected } from '../selectedContext';
import {
  AutoCorrelationContext,
  AutoCorrelationStep,
  DEFAULT_GRAD_DIFF,
  ScopeSampleUids
} from './context';

export {
  useAutoCorrelation,
  useAutoCorrelationOptional,
  DEFAULT_GRAD_DIFF
} from './context';
export type {
  AutoCorrelationStep,
  AutoCorrelationFamily,
  ScopeSampleUids
} from './context';

const emptyScope = (): ScopeSampleUids => ({
  liao: [],
  lambda: [],
  water_oil: [],
  gas_oil: []
});

const SCOPE_MODES: AutoCorrelationScopeMode[] = [
  'all',
  'multi_analysis',
  'current_correlation',
  'manual',
  'filtered'
];

const parseScopeParam = (
  value: string | null
): AutoCorrelationScopeMode | null =>
  value && SCOPE_MODES.includes(value as AutoCorrelationScopeMode)
    ? (value as AutoCorrelationScopeMode)
    : null;

/**
 * Holds the Auto Correlation wizard state (scope, run inputs, report,
 * overrides) at the AppLayout level, scoped to the active project — the
 * same pattern as MultiAnalysisProvider. Deep links with
 * `?auto=1&scope=multi_analysis` auto-open the wizard.
 */
export const AutoCorrelationProvider = ({
  children
}: React.PropsWithChildren) => {
  const location = useLocation();
  const projectUid = new URLSearchParams(location.search).get('project');

  const { selectedInfo, setSelectedInfo } = useSelected();
  const { data, updateData } = useAppContext();

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<AutoCorrelationStep>('scope');
  const [scopeMode, setScopeMode] = useState<AutoCorrelationScopeMode>('all');
  const [scopeSampleUids, setScopeSampleUids] = useState<ScopeSampleUids>(
    emptyScope
  );
  const [xVariable, setXVariable] = useState<XAxisVariable>('fzi');
  const [gradDiff, setGradDiff] = useState<number>(DEFAULT_GRAD_DIFF);
  const [endpointSource, setEndpointSource] =
    useState<CorrelationEndpointSource>('lab');
  const [report, setReport] = useState<AutoCorrelationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<
    Record<string, SelectedFziFunctionsOptions>
  >({});
  const [activeCombinationKey, setActiveCombinationKey] =
    useState<AutoCombinationKey>('recommended');

  // Keep run inputs in sync with the loaded project's correlation defaults.
  useEffect(() => {
    setXVariable(
      selectedInfo.project?.correlation?.preferences?.x_variable ?? 'fzi'
    );
    const projectGradDiff = selectedInfo.project?.correlation?.grad_diff;
    setGradDiff(
      typeof projectGradDiff === 'number' && Number.isFinite(projectGradDiff)
        ? projectGradDiff
        : DEFAULT_GRAD_DIFF
    );
    setEndpointSource(
      selectedInfo.project?.correlation?.preferences?.endpoint_source ?? 'lab'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInfo.project?.uid]);

  // The wizard state is project-scoped: reset everything when ?project=
  // changes, then honour deep-link params (?auto=1&scope=multi_analysis) and
  // quietly restore the latest persisted report (used by the "Auto" badges).
  const prevProjectRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (prevProjectRef.current === projectUid) return;
    prevProjectRef.current = projectUid;

    setStep('scope');
    setScopeMode('all');
    setScopeSampleUids(emptyScope());
    setReport(null);
    setOverrides({});
    setActiveCombinationKey('recommended');
    setError(null);
    setLoading(false);

    const params = new URLSearchParams(location.search);
    const autoOpen = params.get('auto') === '1';
    const scopeFromUrl = parseScopeParam(params.get('scope'));
    if (scopeFromUrl) setScopeMode(scopeFromUrl);
    setIsOpen(autoOpen);

    if (!projectUid) return;
    let cancelled = false;
    getLatestAutoCorrelation(projectUid)
      .then(latest => {
        if (!cancelled && latest) setReport(latest);
      })
      .catch(() => {
        /* No persisted report yet — nothing to restore. */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUid]);

  const openModal = useCallback(() => {
    // Presented as a tab: keep the current step so switching between the
    // Manual and Auto tabs doesn't lose the user's place in the wizard.
    setError(null);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const setScope = useCallback(
    (mode: AutoCorrelationScopeMode, uids: ScopeSampleUids) => {
      setScopeMode(mode);
      setScopeSampleUids(uids);
    },
    []
  );

  const setOverride = useCallback(
    (parameter: string, type: SelectedFziFunctionsOptions | null) => {
      setOverrides(prev => {
        const next = { ...prev };
        if (type === null) {
          delete next[parameter];
        } else {
          next[parameter] = type;
        }
        return next;
      });
    },
    []
  );

  /** The selection apply() persists: recommendation, then user overrides. */
  const stagedSelection = useMemo(() => {
    const selection: Record<string, SelectedFziFunctionsOptions> = {};
    if (!report) return selection;
    Object.values(report.parameters).forEach(parameter => {
      const choice =
        overrides[parameter.parameter] ??
        parameter.recommended ??
        parameter.best_r_squared;
      if (choice) selection[parameter.parameter] = choice;
    });
    return selection;
  }, [report, overrides]);

  const run = useCallback(async () => {
    const uid = projectUid ?? selectedInfo.project?.uid;
    if (!uid || loading) return;

    setLoading(true);
    setError(null);
    try {
      const result = await runAutoCorrelation(uid, {
        x_variable: xVariable,
        liao_samples_uid: scopeSampleUids.liao.length
          ? scopeSampleUids.liao
          : null,
        lambda_samples_uid: scopeSampleUids.lambda.length
          ? scopeSampleUids.lambda
          : null,
        water_oil_samples_uid: scopeSampleUids.water_oil.length
          ? scopeSampleUids.water_oil
          : null,
        gas_oil_samples_uid: scopeSampleUids.gas_oil.length
          ? scopeSampleUids.gas_oil
          : null,
        grad_diff: gradDiff,
        endpoint_source: endpointSource,
        scope_mode: scopeMode,
        scope_filters: {}
      });
      setReport(result);
      setOverrides({});
      setActiveCombinationKey('recommended');
      setStep('review');
    } catch (err) {
      const text =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to run the auto correlation.';
      setError(text);
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [
    projectUid,
    selectedInfo.project?.uid,
    loading,
    xVariable,
    gradDiff,
    endpointSource,
    scopeMode,
    scopeSampleUids
  ]);

  const apply = useCallback(async () => {
    const uid = projectUid ?? selectedInfo.project?.uid;
    if (!uid || !report || loading) return;

    setLoading(true);
    setError(null);
    try {
      const updatedProject = await applyAutoCorrelation(uid, {
        report_uid: report.uid,
        x_variable: report.x_variable,
        selection: stagedSelection,
        rerun_correlation: true,
        grad_diff: gradDiff
      });

      // Mirror the manual correlation flow: the response is the full updated
      // project, so the Correlation / Kr x Pc pages pick it up immediately.
      setSelectedInfo({ project: updatedProject });
      updateData({
        projects: (data.projects ?? []).map(project =>
          project.uid === updatedProject.uid ? updatedProject : project
        )
      });
      setReport(prev =>
        prev
          ? {
              ...prev,
              applied: true,
              applied_selection: stagedSelection,
              applied_x_variable: prev.x_variable,
              applied_zone_values: (
                updatedProject.correlation?.zones ?? []
              ).reduce<Record<string, ZoneValues>>((snapshot, zone) => {
                const values =
                  zone.zone_values_by_x_axis?.[prev.x_variable] ??
                  (prev.x_variable === 'fzi' ? zone.zone_values : null);
                if (values) snapshot[zone.uid] = values;
                return snapshot;
              }, {})
            }
          : prev
      );
      setIsOpen(false);
      message.success('Auto correlation applied.');
    } catch (err) {
      const text =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to apply the auto correlation.';
      setError(text);
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [
    projectUid,
    selectedInfo.project?.uid,
    report,
    loading,
    stagedSelection,
    gradDiff,
    setSelectedInfo,
    updateData,
    data.projects
  ]);

  const restoreLatest = useCallback(async () => {
    const uid = projectUid ?? selectedInfo.project?.uid;
    if (!uid || loading) return;

    setLoading(true);
    setError(null);
    try {
      const latest = await getLatestAutoCorrelation(uid);
      if (!latest) {
        setError('No auto correlation report found for this project.');
        return;
      }
      setReport(latest);
      setEndpointSource(latest.scope.endpoint_source ?? 'lab');
      setOverrides({});
      setActiveCombinationKey('recommended');
      setStep('review');
    } catch (err) {
      setError('No auto correlation report found for this project.');
    } finally {
      setLoading(false);
    }
  }, [projectUid, selectedInfo.project?.uid, loading]);

  return (
    <AutoCorrelationContext.Provider
      value={{
        isOpen,
        step,
        scopeMode,
        scopeSampleUids,
        xVariable,
        gradDiff,
        endpointSource,
        report,
        loading,
        error,
        overrides,
        activeCombinationKey,
        stagedSelection,
        openModal,
        closeModal,
        setStep,
        setScope,
        setXVariable,
        setGradDiff,
        setEndpointSource,
        setOverride,
        setActiveCombinationKey,
        run,
        apply,
        restoreLatest
      }}
    >
      {children}
    </AutoCorrelationContext.Provider>
  );
};
